import { useState } from "react";
import {
    Box, TextField, MenuItem, Button, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, Typography, Alert,
    CircularProgress, Stack, Collapse, IconButton, Dialog,
    DialogTitle, DialogContent, DialogContentText, DialogActions
} from "@mui/material";
import {
    Search as SearchIcon,
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon,
    Delete as DeleteIcon
} from "@mui/icons-material";
import { Customer, Location, InspectionData, SearchFilters } from "../../Types/InspectionTypes";

interface LoadSheetTabProps {
    customers: Customer[];
    locations: Location[];
    onLoadSheet: (inspectionId: number) => void;
    onSearch: (filters: SearchFilters) => Promise<InspectionData[]>;
    isMobile?: boolean;
    isAdmin?: boolean;
    onDeleteSheet?: (inspectionId: number) => Promise<void>;
}

const LoadSheetTab: React.FC<LoadSheetTabProps> = ({
    customers,
    locations,
    onLoadSheet,
    onSearch,
    isMobile = false,
    isAdmin = false,
    onDeleteSheet,
}) => {
    const [filters, setFilters] = useState<SearchFilters>({
        customer: '',
        locationId: '',
        dateFrom: '',
        dateTo: ''
    });
    const [searchResults, setSearchResults] = useState<InspectionData[]>([]);
    const [loading, setLoading] = useState(false);
    const [hasSearched, setHasSearched] = useState(false);
    const [resultsExpanded, setResultsExpanded] = useState(true);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [inspectionToDelete, setInspectionToDelete] = useState<number | null>(null);

    const handleFilterChange = (field: keyof SearchFilters, value: string) => {
        setFilters(prev => ({ ...prev, [field]: value }));
    };

    const handleSearch = async () => {
        setLoading(true);
        setHasSearched(true);
        try {
            const results = await onSearch(filters);
            setSearchResults(results);
        } catch (error) {
            console.error('Error searching inspections:', error);
            setSearchResults([]);
        } finally {
            setLoading(false);
        }
    };

    const handleClearFilters = () => {
        setFilters({
            customer: '',
            locationId: '',
            dateFrom: '',
            dateTo: ''
        });
        setSearchResults([]);
        setHasSearched(false);
        setResultsExpanded(true);
    };

    const handleLoadSheetClick = (inspectionId: number) => {
        setResultsExpanded(false);
        onLoadSheet(inspectionId);
    };

    const handleDeleteClick = (inspectionId: number, event: React.MouseEvent) => {
        event.stopPropagation();
        setInspectionToDelete(inspectionId);
        setDeleteDialogOpen(true);
    };

    const handleDeleteConfirm = async () => {
        if (inspectionToDelete && onDeleteSheet) {
            await onDeleteSheet(inspectionToDelete);
            await handleSearch();
        }
        setDeleteDialogOpen(false);
        setInspectionToDelete(null);
    };

    const handleDeleteCancel = () => {
        setDeleteDialogOpen(false);
        setInspectionToDelete(null);
    };

    const toggleResultsExpanded = () => {
        setResultsExpanded(prev => !prev);
    };

    const getLocationName = (locationId: number) => {
        const location = locations.find(loc => loc.id === locationId);
        return location ? location.locationName : 'Unknown';
    };

    const formatDate = (dateString: string) => {
        if (!dateString) return 'N/A';
        try {
            return new Date(dateString).toLocaleDateString();
        } catch {
            return dateString;
        }
    };

    return (
        <Box>
            <Typography variant="h6" gutterBottom>
                Search Inspection Sheets
            </Typography>

            {/* Filter Controls */}
            <Stack spacing={2} sx={{ mb: 3 }}>
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
                        gap: 2,
                    }}
                >
                    {/* Account Filter */}
                    <TextField
                        select
                        label="Account"
                        value={filters.customer}
                        onChange={(e) => handleFilterChange('customer', e.target.value)}
                        size="small"
                        fullWidth
                    >
                        <MenuItem value="">All Accounts</MenuItem>
                        {customers.map((c) => (
                            <MenuItem key={c.customer} value={c.customer}>
                                {c.customer}
                            </MenuItem>
                        ))}
                    </TextField>

                    {/* Location Filter */}
                    <TextField
                        select
                        label="Storage"
                        value={filters.locationId}
                        onChange={(e) => handleFilterChange('locationId', e.target.value)}
                        size="small"
                        fullWidth
                    >
                        <MenuItem value="">All Locations</MenuItem>
                        {locations.map((loc) => (
                            <MenuItem key={loc.id} value={loc.id.toString()}>
                                {loc.locationName}
                            </MenuItem>
                        ))}
                    </TextField>

                    {/* Date From */}
                    <TextField
                        type="date"
                        label="Date Serviced From"
                        value={filters.dateFrom}
                        onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                        size="small"
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                    />

                    {/* Date To */}
                    <TextField
                        type="date"
                        label="Date Serviced To"
                        value={filters.dateTo}
                        onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                        size="small"
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                    />
                </Box>

                {/* Action Buttons */}
                <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button
                        variant="contained"
                        startIcon={<SearchIcon />}
                        onClick={handleSearch}
                        disabled={loading}
                        fullWidth={isMobile}
                    >
                        Search
                    </Button>
                    <Button
                        variant="outlined"
                        onClick={handleClearFilters}
                        disabled={loading}
                    >
                        Clear Filters
                    </Button>
                </Box>
            </Stack>

            {/* Loading State */}
            {loading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                </Box>
            )}

            {/* Search Results */}
            {!loading && hasSearched && (
                <>
                    {searchResults.length === 0 ? (
                        <Alert severity="info">
                            No inspection sheets found matching your criteria.
                        </Alert>
                    ) : (
                        <Box>
                            {/* Results Header with Toggle */}
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    mb: 1,
                                    p: 1,
                                    backgroundColor: 'grey.800',
                                    borderRadius: 1,
                                }}
                            >
                                <Typography variant="subtitle1" fontWeight="bold">
                                    Search Results ({searchResults.length} found)
                                </Typography>
                                <IconButton
                                    onClick={toggleResultsExpanded}
                                    size="small"
                                    sx={{
                                        transition: 'transform 0.3s',
                                    }}
                                >
                                    {resultsExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                </IconButton>
                            </Box>

                            {/* Collapsible Results Table */}
                            <Collapse in={resultsExpanded}>
                                <TableContainer sx={{ maxHeight: isMobile ? 400 : 500 }}>
                                    <Table stickyHeader size={isMobile ? "small" : "medium"}>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell><strong>ID</strong></TableCell>
                                                <TableCell><strong>Account</strong></TableCell>
                                                <TableCell><strong>Location</strong></TableCell>
                                                <TableCell><strong>Date Serviced</strong></TableCell>
                                                <TableCell><strong>Date Created</strong></TableCell>
                                                <TableCell align="right"><strong>Action</strong></TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {searchResults.map((inspection) => (
                                                <TableRow
                                                    key={inspection.id}
                                                    hover
                                                    sx={{ cursor: 'pointer' }}
                                                    onClick={() => handleLoadSheetClick(inspection.id)}
                                                >
                                                    <TableCell>{inspection.id}</TableCell>
                                                    <TableCell>{inspection.customer}</TableCell>
                                                    <TableCell>{getLocationName(inspection.location)}</TableCell>
                                                    <TableCell>{formatDate(inspection.dateServiced)}</TableCell>
                                                    <TableCell>{formatDate(inspection.dateCreated)}</TableCell>
                                                    <TableCell align="right">
                                                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                                                            <Button
                                                                size="small"
                                                                variant="outlined"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleLoadSheetClick(inspection.id);
                                                                }}
                                                            >
                                                                Load
                                                            </Button>
                                                            {isAdmin && onDeleteSheet && (
                                                                <IconButton
                                                                    size="small"
                                                                    color="error"
                                                                    onClick={(e) => handleDeleteClick(inspection.id, e)}
                                                                    title="Delete inspection"
                                                                >
                                                                    <DeleteIcon fontSize="small" />
                                                                </IconButton>
                                                            )}
                                                        </Stack>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Collapse>
                        </Box>
                    )}
                </>
            )}

            {/* Initial State */}
            {!loading && !hasSearched && (
                <Alert severity="info" sx={{ mt: 2 }}>
                    Use the filters above to search for inspection sheets. Leave filters empty to see all sheets.
                </Alert>
            )}

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={deleteDialogOpen}
                onClose={handleDeleteCancel}
                aria-labelledby="delete-sheet-dialog-title"
                aria-describedby="delete-sheet-dialog-description"
            >
                <DialogTitle id="delete-sheet-dialog-title">
                    Delete Inspection Sheet?
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="delete-sheet-dialog-description">
                        Are you sure you want to delete inspection sheet ID: {inspectionToDelete}?
                        This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDeleteCancel} color="primary">
                        Cancel
                    </Button>
                    <Button onClick={handleDeleteConfirm} color="error" variant="contained" autoFocus>
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default LoadSheetTab;
